/** @format */

import React, { useState, useRef, useEffect } from "react";
import { FaCloudUploadAlt, FaTrash } from "react-icons/fa";

interface ImageUploadProps {
  id: string;
  name: string;
  label: string;
  required?: boolean;
  value?: File | string | null;
  onChange: (file: File | null) => void;
  error?: boolean;
  errorMessage?: string;
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  id,
  name,
  label,
  required = false,
  value,
  onChange,
  error = false,
  errorMessage,
  className = "",
}) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }

    if (typeof value === "string") {
      setPreviewUrl(value);
    } else if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [value]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("image/")) {
        onChange(file);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      onChange(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div className={`mb-4 ${className}`}>
      <label htmlFor={id} className="textInputLabel">
        {label}
        {required && <span className="textInputLabelRequired">*</span>}
      </label>

      {!previewUrl ? (
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${
            dragActive
              ? "border-myButtonBg bg-gray-50 scale-[1.02]"
              : "border-gray-300 hover:border-gray-400 bg-gray-50"
          } ${error ? "border-red-600" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <div className="p-3 bg-white rounded-full shadow-sm mb-3">
            <FaCloudUploadAlt className="text-3xl text-myButtonBg" />
          </div>
          <p className="text-sm text-myLabelText font-semibold mb-1">
            Click to upload or drag and drop
          </p>
          <p className="text-xs text-gray-500">
            SVG, PNG, JPG or GIF (max. 800x400px)
          </p>
          <input
            ref={inputRef}
            type="file"
            id={id}
            name={name}
            className="hidden"
            accept="image/*"
            onChange={handleChange}
          />
        </div>
      ) : (
        <div className="relative w-full h-40 rounded-xl overflow-hidden border border-gray-200 group shadow-sm">
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center backdrop-blur-sm">
            <button
              type="button"
              onClick={handleRemove}
              className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-full transition-transform hover:scale-110 shadow-lg"
              title="Remove image"
            >
              <FaTrash size={20} />
            </button>
          </div>
        </div>
      )}
      {error && (
        <p className="textInputErrorMessage">
          {errorMessage || `Field is required, ${label}`}
        </p>
      )}
    </div>
  );
};

export default ImageUpload;
